import { Component, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { firstValueFrom, Subject, takeUntil } from 'rxjs';
import { AuthService } from '../../services/auth.service';
import { KeycloakService } from 'keycloak-angular';
import { BaseService } from '../../services/base.service';
import { environment } from '../../../environments/environment';
import { LoginForm } from './login.interface';
import { IError } from '../../interfaces';
import { DEFAULT_ERROR, ERROR_MESSAGES, activeTranslations } from '../../enum/constants';
import { NotificationService } from '../../services/notifications.service';
import { ModalService } from '../../services/modal.service';
import { ResetPasswordComponent } from '../../components/modals/reset-password/reset-password.component';
import { decodeBase64, trimValidator } from '../../helpers';

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class LoginComponent implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<null>();

  constructor(
    private readonly router: Router,
    private readonly authService: AuthService,
    private readonly keycloakService: KeycloakService,
    private readonly notificationService: NotificationService,
    private readonly modalService: ModalService,
    public readonly baseService: BaseService,
    private route: ActivatedRoute,
  ) {}

  public homeUrl: string = `${window.location.protocol}//${environment.web_url}`;
  public loginForm!: FormGroup<LoginForm>;
  public loading: boolean = true;
  public isLogging: boolean = false;

  public ngOnInit() {
    this.initForm();
    this.authService.initUtm();
    if (!this.keycloakService.isLoggedIn()) {
      this.loading = false;
      return;
    }

    this.baseService
      .getProfile()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          response?.phone ? this.router.navigate(['/companies']) : this.router.navigate(['/additional']);
        },
        error: (error) => {
          console.log(error);
          this.loading = false;
        },
      });
  }

  public ngOnDestroy(): void {
    this.destroy$.next(null);
    this.destroy$.complete();
  }

  public onSubmit() {
    this.isLogging = true;

    if (this.loginForm.invalid) {
      this.loginForm.markAllAsTouched();
      this.isLogging = false;
      return;
    }

    const payload = this.loginForm.getRawValue();

    this.authService.login(payload).subscribe({
      next: async (res) => {
        const token = res.access_token;
        const refreshToken = res.refresh_token;
        const keycloakOptions = {
          config: { ...environment.keycloak },
          initOptions: {
            checkLoginIframe: false,
            token: token,
            refreshToken: refreshToken,
          },
        };
        try {
          await this.keycloakService.init(keycloakOptions);

          localStorage.setItem('token', token);
          localStorage.setItem('refreshToken', refreshToken);
          const profile = await firstValueFrom(this.baseService.getProfile());
          if (!profile?.phone) {
            this.isLogging = false;
            await this.router.navigate(['/additional']);
            return;
          }

          if (this.baseService.getCompany()) {
            const redirectCode = this.route.snapshot.queryParamMap.get('redirect_code');
            if (redirectCode) {
              const redirectUrl = decodeBase64(redirectCode);

              if (redirectUrl) window.location.href = redirectUrl;
              else this.router.navigate(['/crm']);
            }
          } else {
            this.router.navigate(['/companies']);
          }
        } catch {
          this.router.navigate(['/login']);
        }
        this.isLogging = false;
      },
      error: (e) => {
        (e.error?.errors || []).forEach((err: IError) => {
          if (err.code === 'NOT_VERIFIED_PHONE') {
            this.authService.openVerifyPhoneModal(payload.email, payload.password);
            return;
          }
          this.notificationService.showError(
            activeTranslations.notifications_texts.error.error,
            ERROR_MESSAGES[err.code] || ERROR_MESSAGES[DEFAULT_ERROR],
          );
        });
        this.isLogging = false;
      },
    });
  }

  public onResetPassword() {
    const dialogData = {
      title: 'reset_password.title',
      translate: true,
      component: ResetPasswordComponent,
      data: {
        email: this.loginForm.controls.email.getRawValue(),
      },
    };

    this.modalService.openModal(dialogData);
  }

  public onRegister() {
    this.router.navigate(['/register']);
  }

  private initForm() {
    this.loginForm = new FormGroup<LoginForm>({
      email: new FormControl('', [Validators.required, trimValidator]),
      password: new FormControl('', [Validators.required, trimValidator]),
    });
  }
}
